import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import {
  useFetchKnowledgeBase,
  useSetFaqsFiles,
} from '../../../../hooks/apiHooks/twinHooks';
import { useTwinData } from '../../../../hooks/storeHooks/useTwin';
import { IKnowledgeFile } from '../../../../store/reducers/twinReducer';
import { OidcUser } from '../../../../types/oidcUser';
import { uploadFile } from '../../../../utils/fileUploader';
import { getOrganizationId } from '../../../../utils/getOrganization';
import { DnDFile } from '../DnDFile';
import { Heading } from '../Heading';
import { KnowledgeFileItem } from './KnowledgeFileItem';

interface KnowledgeBaseSectionProps {
  goNext: () => void;
  mentorId: string;
  isReadyStatus: boolean;
}

const KnowledgeBaseSection: React.FC<KnowledgeBaseSectionProps> = memo(
  ({ goNext, mentorId, isReadyStatus }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const orgId = getOrganizationId(user as OidcUser);

    const dndRef = useRef<HTMLInputElement>(null);

    const {
      uploadedKnowledge,
      updateKnowledgeFileStatus,
      setUploadedKnowledge,
    } = useTwinData();

    const { mutateAsync: setFaqsFilesAPI } = useSetFaqsFiles();
    const { data: knowledgeFiles, isFetching: isKnowledgeFilesFetching } =
      useFetchKnowledgeBase({ mentorId });

    useEffect(() => {
      if (knowledgeFiles?.data) {
        const filesRecord = knowledgeFiles.data.reduce<
          Record<string, IKnowledgeFile>
        >((acc, f) => {
          acc[f.fileName] = {
            ...f,
            id: f.id,
            fileName: f.fileName,
            fileType: f.fileType || '',
            fileExtension: f.fileExtension || '',
            status: 'uploaded' as const,
            file: null,
          };
          return acc;
        }, {});
        setUploadedKnowledge(filesRecord);
      }
    }, [knowledgeFiles]);

    const runUploading = async (file: File, retry = false) => {
      if (retry) {
        updateKnowledgeFileStatus({
          fileName: file.name,
          status: 'uploading',
          errorMessage: '',
          file: file,
          id: file.name,
        });
      }

      try {
        const response = await uploadFile(file, orgId);

        if (mentorId && isReadyStatus) {
          const setFaQResp = await setFaqsFilesAPI({
            urls: [{ url: response.uploadUrl || '', id: response.id }],
            mentorId,
          });

          updateKnowledgeFileStatus({
            fileName: file.name,
            status: 'uploaded',
            file: null,
            uploadUrl: response.uploadUrl,
            id: setFaQResp?.data[0].id,
            uploadId: setFaQResp?.data[0].uploadId,
          });
          toast.success(t('twin.knowledgeUpdated'));
          return;
        }
        updateKnowledgeFileStatus({
          fileName: file.name,
          status: 'uploaded',
          file: null,
          uploadUrl: response.uploadUrl,
          id: response.id,
          uploadId: response.id,
        });
      } catch {
        updateKnowledgeFileStatus({
          fileName: file.name,
          status: 'error',
          errorMessage: 'Failed to upload file',
          file: file,
          id: file.name,
        });
      }
    };

    const isContinueDisabled =
      Object.values(uploadedKnowledge).some(
        (file) => file.status === 'uploading',
      ) || !Object.keys(uploadedKnowledge).length;

    const clearInput = () => {
      if (dndRef.current) {
        dndRef.current.value = '';
      }
    };

    const hideSkipButton = !Object.keys(uploadedKnowledge).length;

    return (
      <div className="max-w-full w-[600px] mb-auto sm:mb-0">
        <Heading
          title={t('twin.knowledgeBaseTitle')}
          subtitle={t('twin.knowledgeBaseSubtitle')}
        />

        <DnDFile
          uploadFile={runUploading}
          disabled={isKnowledgeFilesFetching}
          ref={dndRef}
        />
        {isKnowledgeFilesFetching && (
          <div className="mt-4 flex  justify-center">
            <AnimatedSpinner size={24} />
          </div>
        )}
        <ul className="mt-4 w-full flex flex-col gap-2 min-1100:max-h-[200px] max-h-auto overflow-y-auto">
          {Object.values(uploadedKnowledge).map((file) => (
            <li key={file.fileName} className="flex gap-2">
              <KnowledgeFileItem
                storedFile={file}
                retryUpload={runUploading}
                mentorId={mentorId}
                isReadyStatus={isReadyStatus}
                orgId={orgId}
                clearInput={clearInput}
              />
            </li>
          ))}
        </ul>

        {isReadyStatus ? (
          <div className="flex gap-4 justify-center mt-8">
            <Link
              data-testid="edit-knowledge-back-btn"
              to="/twin"
              className="btn btn-primary sm:!grow-0 !grow">
              <i className="fa-solid fa-arrow-left mr-2" />
              Back
            </Link>
          </div>
        ) : (
          <div className="flex gap-4 justify-center mt-8">
            {hideSkipButton && (
              <button
                data-testid="linkedin-skip-btn"
                className="btn btn-secondary sm:!grow-0 !grow"
                onClick={goNext}
                disabled={false}>
                {t('twin.skipForNow')}
              </button>
            )}
            <button
              data-testid="linkedin-continue-btn"
              onClick={goNext}
              disabled={isContinueDisabled}
              className={`btn btn-primary sm:!grow-0  ${
                !hideSkipButton ? 'grow-0' : '!grow'
              }`}>
              {t('twin.continue')}
              <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        )}
      </div>
    );
  },
);

export { KnowledgeBaseSection };
